import { useNavigate } from "react-router";
import { api } from "../../api";
import { useQueryClient } from "@tanstack/react-query";
import { ThreadInitState } from "../types/ThreadInitState";
import { OrgKey, ThreadSummary, UIThread } from "../../generated/core-api";

export function useCreateThread(workspaceId: string) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const createThread = async (
    userIntent: string,
    knowledgeFromOrgs: OrgKey[]
  ) => {
    const thread_id = crypto.randomUUID();

    const thread = await api.createThread(workspaceId, {
      workspace_id: workspaceId,
      thread_id,
      title: userIntent || "Untitled thread",
      knowledge_from_orgs: knowledgeFromOrgs,
    });

    // Seed the individual thread cache so ThreadView doesn't need a follow-up GET.
    queryClient.setQueryData<UIThread>(
      ["workspace", workspaceId, "thread", thread_id],
      thread
    );

    // Append to the thread list cache so the sidebar updates without a refetch.
    queryClient.setQueryData<ThreadSummary[]>(
      ["workspace", workspaceId, "thread"],
      (current) => {
        const summary: ThreadSummary = {
          workspace_id: thread.workspace_id,
          thread_id: thread.thread_id,
          title: thread.title,
          created_at: thread.created_at,
          knowledge_from_orgs: thread.knowledge_from_orgs,
        };
        return current ? [...current, summary] : [summary];
      }
    );

    navigate(`/${thread_id}`, {
      state: {
        initialKnowledgeFromOrgs: knowledgeFromOrgs,
        userIntent,
      } satisfies ThreadInitState,
    });
  };

  return { createThread };
}
